import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { OtpChallenge } from '../../lib/auth/auth-contract.js';
import { formatWait, useCountdown } from '../../lib/auth/use-countdown.js';
import { Button } from '@tailoring-crm/ui';
import { FormError } from './auth-fields.js';
import { OtpInput } from './otp-input.js';

/**
 * "Enter the code we sent you" — the same screen for signing up and for resetting a password.
 *
 * Two details carry it. The masked number is shown back (`98•••••210`), because the commonest
 * reason a code never arrives is that it went to the wrong number and nobody could tell. And
 * the resend button counts down to the server's own `resendAfter` instant rather than to a
 * local timer, so it can neither unlock early into a 429 nor sulk forever on a phone whose
 * clock drifted while it was locked.
 */
export interface OtpStepProps {
  challenge: OtpChallenge;
  submitting: boolean;
  resending: boolean;
  error: string | null;
  onSubmit: (code: string) => void;
  onResend: () => void;
  onChangeNumber: () => void;
}

export function OtpStep({
  challenge,
  submitting,
  resending,
  error,
  onSubmit,
  onResend,
  onChangeNumber,
}: OtpStepProps): ReactElement {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const waitSeconds = useCountdown(challenge.resendAfter);

  const submit = (value: string): void => {
    if (value.length === challenge.codeLength) onSubmit(value);
  };

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(event) => {
        event.preventDefault();
        submit(code);
      }}
      noValidate
    >
      <p className="text-sm text-muted-foreground">
        {t('auth.codeSentTo', { address: challenge.sentTo })}
      </p>

      <FormError message={error} />

      <OtpInput
        value={code}
        onChange={setCode}
        // Six digits typed with a thumb and then a "Continue" button to find is one tap too
        // many; a complete code submits itself.
        onComplete={submit}
        length={challenge.codeLength}
        disabled={submitting}
        invalid={error !== null}
        autoFocus
        label={t('auth.codeLabel')}
        digitLabel={(position) => t('auth.codeDigit', { position })}
      />

      {challenge.provider === 'console' ? (
        // Development only: the fake SMS provider prints the code to the API log, and a new
        // developer should not have to read the source to discover that.
        <p className="rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-sm text-warning">
          {t('auth.codeInConsole')}
        </p>
      ) : null}

      <Button type="submit" block disabled={submitting || code.length !== challenge.codeLength}>
        {t('auth.verify')}
      </Button>

      <div className="flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={onChangeNumber}
          className="text-primary underline-offset-4 hover:underline"
        >
          {t('auth.changeEmail')}
        </button>

        {waitSeconds > 0 ? (
          <span className="text-muted-foreground" aria-live="polite">
            {t('auth.resendIn', { wait: formatWait(waitSeconds) })}
          </span>
        ) : (
          <button
            type="button"
            onClick={onResend}
            disabled={resending}
            className="text-primary underline-offset-4 hover:underline disabled:opacity-50"
          >
            {t('auth.resend')}
          </button>
        )}
      </div>
    </form>
  );
}
